
import React from "react";
import { PrayerTime } from "@/types/page";
import { ScrollArea } from "@/components/ui/scroll-area";
import SortablePrayerTimeCard from "./SortablePrayerTimeCard";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

interface PrayerTimesListProps {
  prayerTimes: PrayerTime[];
  onUpdate: (updated: PrayerTime) => void;
  onRemove: (id: string) => void;
  onReorder: (items: PrayerTime[]) => void;
}

const PrayerTimesList: React.FC<PrayerTimesListProps> = ({ 
  prayerTimes,
  onUpdate,
  onRemove,
  onReorder
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      // Find the indices of the dragged item and the drop target
      const oldIndex = prayerTimes.findIndex(pt => pt.id === active.id);
      const newIndex = prayerTimes.findIndex(pt => pt.id === over.id);
      
      // Create a new array with the item moved to the new position
      const newItems = [...prayerTimes];
      const [removed] = newItems.splice(oldIndex, 1);
      newItems.splice(newIndex, 0, removed);
      
      // Call the parent handler with the reordered items
      onReorder(newItems);
    }
  };

  if (prayerTimes.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-gray-300 rounded-md bg-gray-50">
        <p className="text-gray-500">לא הוגדרו זמני תפילה.</p>
        <p className="text-gray-500 text-sm">לחץ על הכפתור למטה להוספת זמן חדש.</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px] rounded-md border pr-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={prayerTimes.map(pt => pt.id)}
          strategy={verticalListSortingStrategy}
        >
          {prayerTimes.map((prayerTime) => (
            <SortablePrayerTimeCard
              key={prayerTime.id}
              prayerTime={prayerTime}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </SortableContext>
      </DndContext>
    </ScrollArea>
  );
};

export default PrayerTimesList;
